import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { resultsApi } from '../../api/resultsApi';
import { penaltyCatalogApi } from '../../api/penaltyCatalogApi';
import { TimeDisplayInput } from '../TimeDisplayInput';
import { formatTime } from '../../utils/timeFormat';
import type { Category, Discipline, PenaltyTrackingMode, Team } from '../../types/domain';
import type { Lane, DisciplineType } from '../../types/domain';
import { AGE_GROUP_LABELS } from '../../types/domain';
import type {
  PenaltyCatalogItem,
  PenaltyCategory,
  Result,
  ResultAttempt,
  ResultPenalty,
} from '../../types/results';
import { PENALTY_CATEGORY_LABELS } from '../../types/results';
import { ConfirmDialog } from '../ConfirmDialog';

interface ResultEntryDialogProps {
  open: boolean;
  team: Team | null;
  discipline: Discipline | null;
  category?: Category | null;
  attemptNumber: number;
  parallelTracks?: number;
  competitionId?: number;
  penaltyTrackingMode?: PenaltyTrackingMode;
  existing?: Result | null;
  onClose: () => void;
}

const lanesForDiscipline = (type: DisciplineType, parallelTracks: number): Lane[] => {
  if (type === 'POZARNI_UTOK' || type === 'POZARNI_UTOK_CTIF') return ['LP', 'PP'];
  if (type === 'STAFETA_DVOJIC') return parallelTracks >= 2 ? ['TRACK_1', 'TRACK_2'] : ['TRACK_1'];
  return ['TRACK_1'];
};

const LANE_LABELS_SHORT: Record<Lane, string> = {
  LP: 'LP',
  PP: 'PP',
  TRACK_1: 'Dráha 1',
  TRACK_2: 'Dráha 2',
};

const MAX_TIMER_COUNT = 2;

/** Zaznam jednoho slotu pro Tab UI. */
interface Slot {
  lane: Lane;
  timerIdx: number; // 0-based
  key: string;      // `${lane}-${timerIdx}`
  label: string;    // `LP · Časoměřič 1`
}

export const ResultEntryDialog = ({
  open,
  team,
  discipline,
  category,
  attemptNumber,
  parallelTracks = 2,
  competitionId,
  penaltyTrackingMode,
  existing,
  onClose,
}: ResultEntryDialogProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const lanes = useMemo(
    () => (discipline ? lanesForDiscipline(discipline.type, parallelTracks) : []),
    [discipline, parallelTracks],
  );

  const timeFormat = discipline?.timeFormat ?? 'MM_SS_CC';
  const timerCount = discipline?.useSecondTimer ? MAX_TIMER_COUNT : 1;

  // Display format override — umoznuje uzivateli prepnout SS,cc ↔ M:SS,cc i kdyz disciplina
  // ma natvrdo SS_CC. Format pro ulozena data v ms je neutralni, takze muzeme prepinat
  // bez ztraty dat.
  const [displayFormat, setDisplayFormat] = useState<typeof timeFormat>(timeFormat);
  useEffect(() => {
    // Pri zmene discipliny (otevreni jineho pokusu) resetuj override na default discipliny
    setDisplayFormat(timeFormat);
  }, [timeFormat, discipline?.id]);

  const slots: Slot[] = useMemo(() => {
    const out: Slot[] = [];
    for (const l of lanes) {
      for (let t = 0; t < timerCount; t++) {
        out.push({
          lane: l,
          timerIdx: t,
          key: `${l}-${t}`,
          label: timerCount === 1 ? LANE_LABELS_SHORT[l] : `${LANE_LABELS_SHORT[l]} · Č${t + 1}`,
        });
      }
    }
    return out;
  }, [lanes, timerCount]);

  const isDetailed = penaltyTrackingMode === 'DETAILED';
  const isStafetaDvojicMultiTrack = discipline?.type === 'STAFETA_DVOJIC' && lanes.length >= 2;

  const [isNp, setIsNp] = useState(false);
  const [penaltySeconds, setPenaltySeconds] = useState(0);
  const [penaltyLane, setPenaltyLane] = useState<Lane | null>(null);
  const [note, setNote] = useState('');
  // DETAILED: per-vyskyt zaznamy s pocitadly (klic = catalog id nebo `custom-N`)
  const [penalties, setPenalties] = useState<ResultPenalty[]>([]);
  const [customDraft, setCustomDraft] = useState<{ label: string; seconds: string; lane: Lane | null } | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['penalty-catalog', competitionId, discipline?.id, isDetailed],
    enabled: !!isDetailed && !!competitionId && !!discipline?.id && open,
    queryFn: () =>
      penaltyCatalogApi.list(competitionId!, { disciplineId: discipline!.id, activeOnly: true }),
  });
  const catalog: PenaltyCatalogItem[] = catalogQuery.data ?? [];
  const [attemptsByLane, setAttemptsByLane] = useState<Record<Lane, (number | null)[]>>(
    {} as Record<Lane, (number | null)[]>,
  );
  const [activeSlotIdx, setActiveSlotIdx] = useState(0);
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(false);

  // Drzim aktualni existing v ref aby init neresetoval uzivatelem rozpracovana data
  // pri pravidelnych refetches z React Query (kazdych 5s). Effect se spousti jen pri
  // zmene identity dialogu (open / team / disciplina / cislo pokusu), ne pri zmene
  // reference `existing` z nove API odpovedi.
  const existingRef = useRef(existing);
  existingRef.current = existing;

  useEffect(() => {
    if (!open || !discipline) return;
    const current = existingRef.current;
    if (current) {
      setIsNp(!!current.isNp);
      setPenaltySeconds(current.penaltySeconds ?? 0);
      setPenaltyLane(current.penaltyLane ?? null);
      setNote(current.note ?? '');
      setPenalties(current.penalties ? current.penalties.map((p) => ({ ...p })) : []);
      const init: Record<string, (number | null)[]> = {};
      for (const l of lanes) {
        init[l] = Array(MAX_TIMER_COUNT).fill(null);
        for (const a of current.attempts ?? []) {
          if (a.lane === l && a.timerSlot >= 1 && a.timerSlot <= MAX_TIMER_COUNT) {
            init[l][a.timerSlot - 1] = a.timeMs;
          }
        }
      }
      setAttemptsByLane(init as Record<Lane, (number | null)[]>);
    } else {
      setIsNp(false);
      setPenaltySeconds(0);
      setPenaltyLane(null);
      setNote('');
      setPenalties([]);
      setCustomDraft(null);
      const init: Record<string, (number | null)[]> = {};
      for (const l of lanes) {
        init[l] = Array(MAX_TIMER_COUNT).fill(null);
      }
      setAttemptsByLane(init as Record<Lane, (number | null)[]>);
    }
    setActiveSlotIdx(0);
  }, [open, discipline, lanes, team?.id, attemptNumber]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!team || !discipline) throw new Error('chybi tym/disciplina');
      const attempts: ResultAttempt[] = [];
      for (const l of lanes) {
        const arr = attemptsByLane[l] ?? [];
        for (let i = 0; i < arr.length; i++) {
          const ms = arr[i];
          if (ms != null) {
            attempts.push({
              lane: l,
              timerSlot: i + 1,
              timeMs: ms,
              isNp: false,
              isElectronic: false,
            });
          }
        }
      }
      if (isDetailed) {
        const detailedPenalties: ResultPenalty[] = penalties
          .filter((p) => p.count > 0 && p.seconds > 0)
          .map((p, i) => ({ ...p, sortOrder: i }));
        const detailedSum = detailedPenalties.reduce((s, p) => s + p.seconds * p.count, 0);
        return resultsApi.saveResult({
          teamId: team.id,
          disciplineId: discipline.id,
          attemptNumber,
          isNp,
          penaltySeconds: detailedSum,
          penaltyLane: null,
          note: note || undefined,
          attempts,
          penalties: detailedPenalties,
        });
      }
      // SIMPLE/NONE: jednorazova penalty + volitelna lane (pouze STAFETA_DVOJIC)
      const sendPenaltyLane =
        discipline.type === 'STAFETA_DVOJIC' && lanes.length >= 2 && penaltySeconds > 0
          ? penaltyLane
          : null;
      return resultsApi.saveResult({
        teamId: team.id,
        disciplineId: discipline.id,
        attemptNumber,
        isNp,
        penaltySeconds,
        penaltyLane: sendPenaltyLane,
        note: note || undefined,
        attempts,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Výsledek uložen', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'results'] });
      queryClient.invalidateQueries({ queryKey: ['public'] });
      onClose();
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při ukládání', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => resultsApi.deleteResult(id),
    onSuccess: () => {
      enqueueSnackbar('Výsledek smazán', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'results'] });
      queryClient.invalidateQueries({ queryKey: ['public'] });
      setConfirmEmptyOpen(false);
      onClose();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při mazání', { variant: 'error' }),
  });

  // Pocet skutecne zadanych casu napric drahami (null = neni zadany)
  const hasAnyTime = Object.values(attemptsByLane).some((arr) => arr.some((ms) => ms != null));

  // Doporucene horni meze pro kontrolni varovani — nejsou validacni (ulozeni necham projit),
  // jen UX signal ze zadany cas nedava smysl pro typ discipliny. Ciselne limity
  // jsou velkorysou hranici reality + zalohou pro extremni pripady.
  const sanityMaxMs = useMemo(() => {
    if (!discipline) return null;
    if (discipline.timeFormat === 'SS_CC') return 120_000; // 2 minuty
    return 900_000; // 15 minut (stafety, delsi discipliny)
  }, [discipline]);

  // Preview uredniho casu: per-lane official + agregace dle typu discipliny + penalty
  // Per-draha penalty (penaltyLane) se prictie ke konkretni drahe PRED agregaci min/max.
  // Pri penaltyLane=null se prictie az k vysledku (puvodni chovani).
  // baseMs = uredni cas bez trestnych (raw agregace); totalMs = po pripocteni trestnych.
  const computedTime = useMemo(() => {
    if (!discipline) return { baseMs: null as number | null, totalMs: null as number | null };
    let perLanePenaltyMs: Partial<Record<Lane, number>> = {};
    let globalPenaltyMs = 0;
    if (isDetailed) {
      for (const p of penalties) {
        if (p.count <= 0 || p.seconds <= 0) continue;
        const ms = p.seconds * 1000 * p.count;
        if (p.lane != null) {
          perLanePenaltyMs[p.lane] = (perLanePenaltyMs[p.lane] ?? 0) + ms;
        } else {
          globalPenaltyMs += ms;
        }
      }
    } else if (discipline.type === 'STAFETA_DVOJIC' && lanes.length >= 2 && penaltyLane != null && penaltySeconds > 0) {
      perLanePenaltyMs[penaltyLane] = penaltySeconds * 1000;
    } else {
      globalPenaltyMs = penaltySeconds * 1000;
    }
    const penaltyMs = penaltySeconds * 1000; // for legacy referenced below
    const useLanePenalty =
      !isDetailed && discipline.type === 'STAFETA_DVOJIC' && lanes.length >= 2 && penaltyLane != null && penaltyMs > 0;
    const rawLaneTimes: number[] = [];
    const penaltyLaneTimes: number[] = [];
    let anyLaneMissing = false;
    for (const l of lanes) {
      const slotsArr = attemptsByLane[l] ?? [];
      const valid = slotsArr.filter((v): v is number => v != null && v > 0).sort((a, b) => a - b);
      let laneMs: number | null = null;
      if (valid.length === 1) laneMs = valid[0];
      else if (valid.length === 2) laneMs = valid[0] === valid[1] ? valid[0] : valid[1];
      else if (valid.length >= 3) {
        // edge case (MAX_TIMER_COUNT je 2, ale pro jistotu)
        laneMs = valid[0] === valid[1] ? valid[0] : valid[1] === valid[2] ? valid[1] : valid[1];
      }
      if (laneMs == null) {
        anyLaneMissing = true;
      } else {
        rawLaneTimes.push(laneMs);
        const lp = perLanePenaltyMs[l] ?? 0;
        penaltyLaneTimes.push(laneMs + lp);
      }
    }
    const takeBest = discipline.type === 'STAFETA_DVOJIC';
    const aggregate = (arr: number[]): number | null => {
      if (arr.length === 0) return null;
      if (takeBest) return Math.min(...arr);
      if (discipline.type === 'POZARNI_UTOK' || discipline.type === 'POZARNI_UTOK_CTIF') {
        return !anyLaneMissing ? Math.max(...arr) : null;
      }
      return arr[0];
    };
    // baseMs = uredni cas pres raw lane casy (bez per-lane penalty); informacni pro UI
    const baseMs = aggregate(rawLaneTimes);
    // totalMs = stejna agregace ale z casu vc. per-lane penalty + global penalty
    // Toto presne odpovida BE OfficialTimeCalculator.calculateAttemptFinalTime
    const totalAfterLane = aggregate(penaltyLaneTimes);
    const totalMs = totalAfterLane != null ? totalAfterLane + globalPenaltyMs : null;
    void penaltyMs;
    void useLanePenalty;
    return { baseMs, totalMs };
  }, [attemptsByLane, lanes, discipline, penaltySeconds, penaltyLane, isDetailed, penalties]);

  const handleSaveClick = () => {
    // NP je explicitni volba, prochazi normalni save
    if (isNp) {
      saveMutation.mutate();
      return;
    }
    // Zadny cas neni zadany — zeptej se, misto tichého ulozeni "prazdneho" vysledku
    if (!hasAnyTime) {
      setConfirmEmptyOpen(true);
      return;
    }
    saveMutation.mutate();
  };

  if (!team || !discipline) return null;

  const activeSlot = slots[activeSlotIdx];
  const activeValueMs = activeSlot ? attemptsByLane[activeSlot.lane]?.[activeSlot.timerIdx] ?? null : null;

  const setActiveValue = (ms: number | null) => {
    if (!activeSlot) return;
    setAttemptsByLane((prev) => {
      const arr = [...(prev[activeSlot.lane] ?? Array(MAX_TIMER_COUNT).fill(null))];
      arr[activeSlot.timerIdx] = ms;
      return { ...prev, [activeSlot.lane]: arr };
    });
  };

  const next = () => {
    if (activeSlotIdx < slots.length - 1) setActiveSlotIdx((i) => i + 1);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        {existing ? 'Úprava výsledku' : 'Zápis výsledku'} — {team.name} (č. {team.startNumber})
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {/* Kompaktni header: chipy s kategorii/disciplinou + NP + format toggle */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
              {category && (() => {
                const ageLabel = AGE_GROUP_LABELS[category.ageGroup];
                const label = category.name === ageLabel ? category.name : `${category.name} · ${ageLabel}`;
                return <Chip size="small" label={label} variant="outlined" />;
              })()}
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  discipline.attemptsCount > 1
                    ? `${discipline.name} — pokus ${attemptNumber}/${discipline.attemptsCount}`
                    : discipline.name
                }
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isNp}
                    onChange={(e) => setIsNp(e.target.checked)}
                    color="error"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color={isNp ? 'error' : 'inherit'}>
                    Neplatný (NP)
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              {!isNp && (
                <ToggleButtonGroup
                  size="small"
                  value={displayFormat}
                  exclusive
                  onChange={(_, next) => {
                    if (next) setDisplayFormat(next as typeof timeFormat);
                  }}
                  aria-label="Formát zadání času"
                >
                  <ToggleButton
                    value="SS_CC"
                    sx={{ px: 1.5, py: 0.25, textTransform: 'none', fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    SS,cc
                  </ToggleButton>
                  <ToggleButton
                    value="MM_SS_CC"
                    sx={{ px: 1.5, py: 0.25, textTransform: 'none', fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    M:SS,cc
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
          </Stack>

          {!isNp && (
            <>
              {slots.length > 1 && (
                <Tabs
                  value={activeSlotIdx}
                  onChange={(_, v) => setActiveSlotIdx(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 48 }}
                >
                  {slots.map((s, idx) => {
                    const ms = attemptsByLane[s.lane]?.[s.timerIdx] ?? null;
                    const isLaneGroupStart = idx > 0 && slots[idx - 1].lane !== s.lane;
                    return (
                      <Tab
                        key={s.key}
                        label={
                          <Stack direction="row" spacing={0.75} alignItems="baseline">
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {s.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                color: ms != null ? 'primary.main' : 'text.disabled',
                                fontWeight: ms != null ? 600 : 400,
                              }}
                            >
                              {ms != null ? formatTime(ms, timeFormat) : '—'}
                            </Typography>
                          </Stack>
                        }
                        value={idx}
                        sx={{
                          minHeight: 44,
                          py: 0.5,
                          ...(isLaneGroupStart
                            ? { borderLeft: 2, borderColor: 'divider', ml: 1, pl: 2 }
                            : {}),
                        }}
                      />
                    );
                  })}
                </Tabs>
              )}

              {activeSlot && (
                <TimeDisplayInput
                  key={activeSlot.key}
                  label={slots.length === 1 ? 'Čas' : `Zadáváte: ${activeSlot.label}`}
                  valueMs={activeValueMs}
                  onChangeMs={setActiveValue}
                  format={displayFormat}
                  onNext={activeSlotIdx < slots.length - 1 ? next : undefined}
                  autoFocus
                  hideFormatToggle
                  compact
                />
              )}

              <Divider sx={{ my: 0.5 }} />
              {isDetailed ? (
                <DetailedPenaltyPanel
                  catalog={catalog}
                  catalogLoading={catalogQuery.isLoading}
                  penalties={penalties}
                  setPenalties={setPenalties}
                  showLaneToggle={isStafetaDvojicMultiTrack}
                  customDraft={customDraft}
                  setCustomDraft={setCustomDraft}
                  totalSeconds={penalties.reduce((s, p) => s + p.seconds * p.count, 0)}
                />
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <TextField
                    label="Trestné sekundy"
                    type="number"
                    value={penaltySeconds}
                    onChange={(e) => {
                      const v = Math.max(0, Number(e.target.value) || 0);
                      setPenaltySeconds(v);
                      if (v === 0) setPenaltyLane(null);
                    }}
                    inputProps={{ min: 0, max: 999 }}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}
                  />
                  {discipline.type === 'STAFETA_DVOJIC' && lanes.length >= 2 && penaltySeconds > 0 && (
                    <ToggleButtonGroup
                      size="small"
                      value={penaltyLane ?? 'NONE'}
                      exclusive
                      onChange={(_, next) => {
                        if (next === 'NONE') setPenaltyLane(null);
                        else if (next === 'TRACK_1' || next === 'TRACK_2') setPenaltyLane(next);
                      }}
                      aria-label="Pro kterou drahu plati trestne"
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="NONE" sx={{ px: 1.25, py: 0.25, textTransform: 'none', fontSize: '0.75rem' }}>
                        Společné
                      </ToggleButton>
                      <ToggleButton value="TRACK_1" sx={{ px: 1.25, py: 0.25, textTransform: 'none', fontSize: '0.75rem' }}>
                        Dráha 1
                      </ToggleButton>
                      <ToggleButton value="TRACK_2" sx={{ px: 1.25, py: 0.25, textTransform: 'none', fontSize: '0.75rem' }}>
                        Dráha 2
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  {penaltySeconds > 0 && computedTime.baseMs != null && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="baseline"
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        fontFamily: 'monospace',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                        Úřední čas:
                      </Typography>
                      <Typography sx={{ fontFamily: 'inherit', fontWeight: 600 }}>
                        {formatTime(computedTime.baseMs, displayFormat)}
                      </Typography>
                      <Typography variant="caption" color="warning.main" sx={{ fontFamily: 'inherit', fontWeight: 700 }}>
                        + {penaltySeconds}s
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                        =
                      </Typography>
                      <Typography color="primary.main" sx={{ fontFamily: 'inherit', fontWeight: 800 }}>
                        {formatTime(computedTime.totalMs, displayFormat)}
                      </Typography>
                    </Stack>
                  )}
                  <TextField
                    label="Poznámka"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              )}
              {isDetailed && (
                <TextField
                  label="Poznámka"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  size="small"
                  fullWidth
                />
              )}
              {isDetailed && computedTime.totalMs != null && computedTime.baseMs != null && computedTime.totalMs !== computedTime.baseMs && (
                <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ px: 1.25, py: 0.75, borderRadius: 1, bgcolor: 'action.hover', fontFamily: 'monospace' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>Úřední čas:</Typography>
                  <Typography sx={{ fontFamily: 'inherit', fontWeight: 600 }}>{formatTime(computedTime.baseMs, displayFormat)}</Typography>
                  <Typography variant="caption" color="warning.main" sx={{ fontFamily: 'inherit', fontWeight: 700 }}>
                    + {penalties.reduce((s, p) => s + p.seconds * p.count, 0)}s
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>=</Typography>
                  <Typography color="primary.main" sx={{ fontFamily: 'inherit', fontWeight: 800 }}>{formatTime(computedTime.totalMs, displayFormat)}</Typography>
                </Stack>
              )}
              {sanityMaxMs != null && computedTime.totalMs != null && computedTime.totalMs > sanityMaxMs && (
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                  ⚠️ Zadaný čas {formatTime(computedTime.totalMs, displayFormat)} se zdá nesmyslně
                  vysoký pro disciplínu „{discipline?.name}". Zkontrolujte prosím vstup.
                </Typography>
              )}
            </>
          )}

          {isNp && (
            <TextField
              label="Poznámka"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMutation.isPending}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveClick}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Ukládám…' : 'Uložit'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmEmptyOpen}
        title="Není zadaný čas"
        message={
          existing
            ? 'Nemáte zadaný žádný čas ani označený neplatný pokus. Pokud potvrdíte, dosavadní výsledek bude smazán (družstvo bude vypadat jako by čas ještě nemělo zadán).'
            : 'Nemáte zadaný žádný čas ani označený neplatný pokus. Bez zadání se nic neuloží. Chcete zavřít dialog?'
        }
        confirmText={existing ? 'Smazat výsledek' : 'Zavřít bez uložení'}
        destructive={!!existing}
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmEmptyOpen(false)}
        onConfirm={() => {
          if (existing) {
            deleteMutation.mutate(existing.id);
          } else {
            setConfirmEmptyOpen(false);
            onClose();
          }
        }}
      />
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// DETAILED penalty panel — grupovany seznam katalogovych chyb
// ─────────────────────────────────────────────────────────────────────

interface DetailedPenaltyPanelProps {
  catalog: PenaltyCatalogItem[];
  catalogLoading: boolean;
  penalties: ResultPenalty[];
  setPenalties: React.Dispatch<React.SetStateAction<ResultPenalty[]>>;
  showLaneToggle: boolean;
  customDraft: { label: string; seconds: string; lane: Lane | null } | null;
  setCustomDraft: React.Dispatch<
    React.SetStateAction<{ label: string; seconds: string; lane: Lane | null } | null>
  >;
  totalSeconds: number;
}

const CATEGORY_ORDER: PenaltyCategory[] = ['OBSTACLES', 'HOSE', 'COUPLING', 'EQUIPMENT', 'OTHER', 'CUSTOM'];

const DetailedPenaltyPanel = ({
  catalog,
  catalogLoading,
  penalties,
  setPenalties,
  showLaneToggle,
  customDraft,
  setCustomDraft,
  totalSeconds,
}: DetailedPenaltyPanelProps) => {
  // Skupiny katalogu podle category
  const grouped = useMemo(() => {
    const map = new Map<PenaltyCategory, PenaltyCatalogItem[]>();
    for (const it of catalog) {
      const arr = map.get(it.category) ?? [];
      arr.push(it);
      map.set(it.category, arr);
    }
    return map;
  }, [catalog]);

  // Najdi zaznam v penalties odpovidajici katalogove polozce (podle catalogId)
  const findCatalogPenalty = (catalogId: number): ResultPenalty | undefined =>
    penalties.find((p) => p.catalogId === catalogId);

  const adjustCatalogCount = (item: PenaltyCatalogItem, delta: number) => {
    setPenalties((prev) => {
      const idx = prev.findIndex((p) => p.catalogId === item.id);
      if (idx === -1) {
        if (delta <= 0) return prev;
        return [
          ...prev,
          {
            catalogId: item.id,
            code: item.code,
            label: item.label,
            seconds: item.seconds,
            count: delta,
            lane: null,
            sortOrder: item.sortOrder,
          },
        ];
      }
      const next = [...prev];
      const newCount = next[idx].count + delta;
      if (newCount <= 0) {
        next.splice(idx, 1);
      } else {
        next[idx] = { ...next[idx], count: newCount };
      }
      return next;
    });
  };

  const setLaneForCatalog = (catalogId: number, lane: Lane | null) => {
    setPenalties((prev) => prev.map((p) => (p.catalogId === catalogId ? { ...p, lane } : p)));
  };

  const removeCustom = (idx: number) => {
    setPenalties((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCustom = () => {
    if (!customDraft) return;
    const seconds = Number(customDraft.seconds);
    if (!customDraft.label.trim() || !Number.isFinite(seconds) || seconds <= 0) return;
    setPenalties((prev) => [
      ...prev,
      {
        catalogId: null,
        code: null,
        label: customDraft.label.trim(),
        seconds,
        count: 1,
        lane: customDraft.lane,
        sortOrder: 9999 + prev.length,
      },
    ]);
    setCustomDraft(null);
  };

  // Breakdown per kategorie pro chips
  const breakdown = useMemo(() => {
    const sums = new Map<PenaltyCategory, number>();
    for (const p of penalties) {
      const cat = catalog.find((c) => c.id === p.catalogId)?.category ?? 'CUSTOM';
      sums.set(cat, (sums.get(cat) ?? 0) + p.seconds * p.count);
    }
    return CATEGORY_ORDER.filter((c) => (sums.get(c) ?? 0) > 0).map((c) => ({
      category: c,
      seconds: sums.get(c) ?? 0,
    }));
  }, [penalties, catalog]);

  if (catalogLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Načítám katalog trestných…
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Trestné body — DETAILED
      </Typography>
      {catalog.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Pro tuto disciplínu není definován žádný katalog. Použijte „+ Vlastní položka" nebo přepněte
          soutěž do režimu SIMPLE v nastavení.
        </Typography>
      )}
      {CATEGORY_ORDER.map((cat) => {
        const items = grouped.get(cat) ?? [];
        if (items.length === 0) return null;
        return (
          <Box key={cat}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {PENALTY_CATEGORY_LABELS[cat]}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {items.map((item) => {
                const existing = findCatalogPenalty(item.id);
                const count = existing?.count ?? 0;
                return (
                  <Stack
                    key={item.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: count > 0 ? 'action.selected' : 'transparent',
                      border: 1,
                      borderColor: count > 0 ? 'primary.light' : 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => adjustCatalogCount(item, -1)}
                        disabled={count === 0}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 24, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
                      >
                        {count}
                      </Typography>
                      <IconButton size="small" onClick={() => adjustCatalogCount(item, 1)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                      {item.label}
                    </Typography>
                    <Chip size="small" label={`${item.seconds}s`} color={count > 0 ? 'warning' : 'default'} variant={count > 0 ? 'filled' : 'outlined'} />
                    {showLaneToggle && count > 0 && (
                      <ToggleButtonGroup
                        size="small"
                        value={existing?.lane ?? 'NONE'}
                        exclusive
                        onChange={(_, next) => {
                          if (next == null) return;
                          setLaneForCatalog(item.id, next === 'NONE' ? null : (next as Lane));
                        }}
                      >
                        <ToggleButton value="NONE" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>
                          —
                        </ToggleButton>
                        <ToggleButton value="TRACK_1" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>
                          D1
                        </ToggleButton>
                        <ToggleButton value="TRACK_2" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>
                          D2
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        );
      })}

      {/* Vlastni (ad-hoc) polozky */}
      {penalties.filter((p) => p.catalogId == null).length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {PENALTY_CATEGORY_LABELS.CUSTOM}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {penalties.map((p, idx) => {
              if (p.catalogId != null) return null;
              return (
                <Stack key={idx} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.selected', border: 1, borderColor: 'primary.light' }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{p.label}</Typography>
                  <Chip size="small" label={`${p.seconds}s × ${p.count}`} color="warning" />
                  {showLaneToggle && (
                    <ToggleButtonGroup
                      size="small"
                      value={p.lane ?? 'NONE'}
                      exclusive
                      onChange={(_, next) => {
                        if (next == null) return;
                        setPenalties((prev) => prev.map((x, i) => (i === idx ? { ...x, lane: next === 'NONE' ? null : (next as Lane) } : x)));
                      }}
                    >
                      <ToggleButton value="NONE" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>—</ToggleButton>
                      <ToggleButton value="TRACK_1" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>D1</ToggleButton>
                      <ToggleButton value="TRACK_2" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>D2</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  <Button size="small" color="error" onClick={() => removeCustom(idx)}>Smazat</Button>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Pridat vlastni polozku */}
      {customDraft == null ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCustomDraft({ label: '', seconds: '', lane: null })}
          sx={{ alignSelf: 'flex-start' }}
        >
          Vlastní položka
        </Button>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <TextField
            label="Popis chyby"
            size="small"
            value={customDraft.label}
            onChange={(e) => setCustomDraft({ ...customDraft, label: e.target.value })}
            fullWidth
            autoFocus
          />
          <TextField
            label="Sekund"
            type="number"
            size="small"
            value={customDraft.seconds}
            onChange={(e) => setCustomDraft({ ...customDraft, seconds: e.target.value })}
            inputProps={{ min: 1, max: 999 }}
            sx={{ width: { xs: '100%', sm: 100 }, flexShrink: 0 }}
          />
          {showLaneToggle && (
            <ToggleButtonGroup
              size="small"
              value={customDraft.lane ?? 'NONE'}
              exclusive
              onChange={(_, next) => {
                if (next == null) return;
                setCustomDraft({ ...customDraft, lane: next === 'NONE' ? null : (next as Lane) });
              }}
            >
              <ToggleButton value="NONE" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>—</ToggleButton>
              <ToggleButton value="TRACK_1" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>D1</ToggleButton>
              <ToggleButton value="TRACK_2" sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>D2</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button size="small" variant="contained" onClick={addCustom}>Přidat</Button>
          <Button size="small" onClick={() => setCustomDraft(null)}>Zrušit</Button>
        </Stack>
      )}

      {/* Souhrn */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">Celkem trestné:</Typography>
        <Chip size="small" label={`+${totalSeconds}s`} color={totalSeconds > 0 ? 'warning' : 'default'} sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
        {breakdown.map((b) => (
          <Chip
            key={b.category}
            size="small"
            label={`${PENALTY_CATEGORY_LABELS[b.category]}: ${b.seconds}s`}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        ))}
      </Stack>
    </Stack>
  );
};
